import type { SyntaxNode } from 'web-tree-sitter';
import type { SymbolImportBinding } from '../../types.js';
import { pushBinding } from './shared.js';

function dottedNameText(node: SyntaxNode): string {
  return node.text.replace(/\s+/g, '');
}

function collectImportStatement(
  out: SymbolImportBinding[],
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
  node: SyntaxNode,
): void {
  const nameNode = node.childForFieldName('name');
  if (!nameNode) return;
  const moduleName = dottedNameText(nameNode);
  const localName = moduleName.split('.').at(-1) ?? moduleName;
  pushBinding(out, filePath, workspaceFiles, moduleName, localName, localName, 'namespace', nameNode);
}

function collectImportFromStatement(
  out: SymbolImportBinding[],
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
  node: SyntaxNode,
): void {
  const moduleNode = node.childForFieldName('module_name');
  const moduleName = moduleNode ? dottedNameText(moduleNode) : '';
  for (const child of node.namedChildren) {
    if (child.type !== 'dotted_name' && child.type !== 'aliased_import') continue;
    if (child === moduleNode) continue;

    if (child.type === 'dotted_name') {
      const sourceName = dottedNameText(child);
      const localName = sourceName.split('.').at(-1) ?? sourceName;
      pushBinding(out, filePath, workspaceFiles, moduleName, localName, sourceName, 'named', child);
      continue;
    }

    const nameNode = child.childForFieldName('name');
    const aliasNode = child.childForFieldName('alias');
    if (!nameNode) continue;
    const sourceName = dottedNameText(nameNode);
    const localName = aliasNode ? aliasNode.text : sourceName.split('.').at(-1) ?? sourceName;
    pushBinding(out, filePath, workspaceFiles, moduleName, localName, sourceName, 'named', aliasNode ?? nameNode);
  }
}

export function extractPythonImportBindings(
  root: SyntaxNode,
  filePath: string,
  workspaceFiles: ReadonlySet<string>,
): SymbolImportBinding[] {
  const out: SymbolImportBinding[] = [];
  for (const node of root.descendantsOfType('import_statement')) {
    collectImportStatement(out, filePath, workspaceFiles, node);
  }
  for (const node of root.descendantsOfType('import_from_statement')) {
    collectImportFromStatement(out, filePath, workspaceFiles, node);
  }
  return out;
}
